import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Tabs, Button, Tag, Space, Spin, Result, message } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faEdit, faRotate } from "@fortawesome/free-solid-svg-icons";
import rewardProgramApi from "../../../api/rewardProgramApi";
import { rewardImageUrl } from "./reward-image-url";
import RewardOverviewTab from "./reward-overview-tab";
import RewardParticipantsTab from "./reward-participants-tab";
import RewardAllocationsTab from "./reward-allocations-tab";
import { PROGRAM_STATUS_META, CAMP_LABELS, fmtUnix } from "./reward-shared-meta";

/**
 * Màn chi tiết 1 đợt thưởng — thay cho modal đối soát cũ (chật, không lọc được).
 * 3 tab: Tổng quan (số liệu + biểu đồ) · Người tham gia (ai đăng ký, lúc nào) ·
 * Phân bổ đơn (sổ RewardOrders). Mỗi tab tự nạp dữ liệu khi được mở.
 */
function RewardProgramDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [failed, setFailed] = useState(false);

    const fetchOverview = useCallback(async () => {
        setLoading(true);
        try {
            const res = await rewardProgramApi.overview(id);
            setData(res.data);
            setFailed(false);
        } catch {
            setFailed(true);
            message.error("Không tải được thông tin đợt");
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => { fetchOverview(); }, [fetchOverview]);

    if (loading && !data) return <div className="flex-1 flex items-center justify-center"><Spin size="large" /></div>;
    if (failed && !data) {
        return (
            <Result
                status="404" title="Không tìm thấy đợt thưởng"
                extra={<Button type="primary" onClick={() => navigate("/reward-program")}>Về danh sách</Button>}
            />
        );
    }

    const program = data.program;
    const statusMeta = PROGRAM_STATUS_META[program.status] || {};

    return (
        <div className="flex-1 flex flex-col p-5 overflow-auto gap-4">
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <Space align="start">
                    {program.thumbnail ? (
                        <img src={rewardImageUrl(program.thumbnail)} alt="" style={{ width: 48, height: 48, borderRadius: 8, objectFit: "cover" }} />
                    ) : <span style={{ fontSize: 40 }}>🎁</span>}
                    <div>
                        <h1 className="text-xl font-bold mb-1">{program.name}</h1>
                        <Space size={4} wrap>
                            <Tag color={statusMeta.color}>{statusMeta.label || program.status}</Tag>
                            <Tag color={program.enabled ? "green" : "default"}>{program.enabled ? "BẬT" : "Tắt"}</Tag>
                            {program.campId ? <Tag color="geekblue">{CAMP_LABELS[program.campId] || program.campId}</Tag> : null}
                            <span className="text-xs text-content-3">
                                {program.slug} · {fmtUnix(program.startTime)} → {fmtUnix(program.endTime)}
                            </span>
                        </Space>
                    </div>
                </Space>
                <Space>
                    <Button icon={<FontAwesomeIcon icon={faArrowLeft} />} onClick={() => navigate("/reward-program")}>Danh sách</Button>
                    <Button icon={<FontAwesomeIcon icon={faRotate} />} loading={loading} onClick={fetchOverview}>Làm mới</Button>
                    <Button type="primary" icon={<FontAwesomeIcon icon={faEdit} />} onClick={() => navigate(`/reward-program/${program.id}/edit`)}>
                        Sửa đợt
                    </Button>
                </Space>
            </div>

            <Tabs
                defaultActiveKey="overview"
                items={[
                    { key: "overview", label: "Tổng quan", children: <RewardOverviewTab program={program} overview={data} /> },
                    {
                        key: "participants",
                        label: `Người tham gia (${Number(data.participants?.total || 0).toLocaleString("vi-VN")})`,
                        children: <RewardParticipantsTab program={program} />,
                    },
                    { key: "allocations", label: "Phân bổ đơn", children: <RewardAllocationsTab program={program} /> },
                ]}
            />
        </div>
    );
}

export default RewardProgramDetail;
